// ============================================================================
// Canonical /pig/sows route contract — the breeding-pig registry and its
// record sub-mode
// ============================================================================
//
// `{view: 'sows', paths: ['/pig/sows/*']}` is a SPLAT row in PIG_ROUTES, and
// `sows` has NO `VIEW_VARIANTS` entry. Both facts together are what make this
// fence necessary rather than prudent: `surfaceForRoutedView` short-circuits on
// the missing variant and answers `view.sows` for the ENTIRE `/pig/sows` space
// — the bare base, the splat base, every malformed id, every nested path, and
// every view-led transition frame from a foreign pathname, which
// `routedLocationFor` canonicalizes onto the bare `/pig/sows`.
//
// `src/app/data/migratedSurfaces/view.sows.ts` already says this surface needs
// a fence, in prose, for exactly these two reasons. This module is that fence.
//
// MEASURED, not reasoned. With `view.sows` added to the ledger and no fence,
// the real resolver answers RELEASED for ALL EIGHTEEN adversarial shapes probed
// — canonical, splat base, child, nested, `%2F`, `%00`, `%0D`, `%C0%80`, `%zz`,
// a bare `%`, a literal backslash, `.`, `..`, a search carrier, a hash carrier,
// and three view-led transition frames (`/`, `/weather`, and the sibling
// `/pig/dailys`) — for both `showForm` values and for both roles that hold pig
// program access. TWELVE of those answer at an OFF-BASE `routedPathname`.
// Seventeen of the eighteen are not the audited direct-entry route.
//
// The same probe run against the LIVE ledger answers RELEASED zero times out of
// one hundred and eight, including at the canonical `/pig/sows`. That is the
// resolver's own gate order: `validation.releasedSurfaces.has(...)` runs BEFORE
// the residual-obligation check, so a `permanent` surface with no ledger row
// resolves LEGACY everywhere. The ledger row is the gate; emptying
// `residualLegacy` only makes a surface releasABLE.
//
// WHY THE SEVERITY IS HIGH HERE. For most released surfaces a non-canonical
// shape is a TRANSIENT frame that App's URL effect repairs a tick later. Half
// of this space has no repair path at all:
//
//   * `src/App.tsx` sets `view === 'sows'` for any pathname starting with
//     `/pig/sows/` (the `isPigSowsSubpath` branch), and
//   * the same file's redirect effect returns EARLY for exactly that pair
//     (`if (view === 'sows' && location.pathname.startsWith('/pig/sows/')) return;`),
//     so it never navigates away from a descendant.
//
// An unfenced RELEASED verdict suppresses recovery, and
// `renderReleasedAuthenticatedSurface` returns `null` for a surface/pathname pair it
// does not recognise. A cold entry on `/pig/sows/<anything malformed>` would
// therefore render nothing and have nothing to heal it — a blank page that
// never repairs itself. Only the bare-base transition frame is repairable, and
// only because `VIEW_TO_PATH.sows` is `/pig/sows` (unlike `weighinsessions`,
// which has no entry at all).
//
// TWO CANONICAL SHAPES, ONE SURFACE. `SURFACE_ROUTERS` declares `view.sows` a
// `sub-mode` surface, not an index/detail pair, so this module is one predicate
// over both modes rather than the two-predicate partition the Dailys routers
// use. SowsView switches on `location.pathname.startsWith('/pig/sows/')`:
//
//   /pig/sows        the registry list (`recordMode` false)
//   /pig/sows/<id>   the record panel, resolved out of the same array the list
//                    renders, with `recordRawId` taken from the FIRST segment
//                    only and decoded once
//
// and `goToBreedingPigs` / `navigateSeq` / `openBreedingPigRecord` are the only
// producers of those pathnames — they navigate to `/pig/sows` and
// `/pig/sows/<encodeURIComponent(id)>` and nothing else.
//
// WHAT THE FIRST-SEGMENT SLICE COSTS WITHOUT A FENCE. `/pig/sows/abc/xyz` gives
// `recordRawId` = `abc`, so an unfenced release would mount the record for
// `abc` under a URL that says `/pig/sows/abc/xyz`; `/pig/sows/` gives the empty
// id and mounts `RecordPageNotFound`. Neither is a route any reviewer audited.
//
// ONE CLAUSE, NOT TWO. The Cattle, Layer and Dailys index fences pair a
// `routedPathname` equality with a raw-location predicate because their views DO
// have a `VIEW_VARIANTS` split, so the routed pathname carries information the
// raw location does not. Here it carries none, for the same reason it carries
// none for `view.weighinsessions`: when the raw path is owned by the splat row,
// `routedLocationFor` returns it unchanged (routed === raw, so the raw predicate
// already sees every malformed descendant), and when the raw path is NOT owned,
// routed collapses to the bare `/pig/sows` while the raw location is a foreign
// path (which the raw predicate also refuses). A second `routedPathname` clause
// would be unfalsifiable here, and an unfalsifiable clause in a fence is worse
// than no clause: it reads as protection. That premise is not left as prose:
// tests/static/pig_sows_fence_static.test.js runs `routedLocationFor` over both
// halves and asserts them — routed === the caller's own object for every owned
// path, routed === the bare base for every foreign one — so the day the
// canonicalization rule changes, this omission is reconsidered rather than
// inherited.
//
// SEARCH AND HASH ARE INSIDE THE CONTRACT, deliberately, and that is where this
// differs from the Dailys and weigh-in-session RECORD fences. The weigh-in
// record separately admits only its audited `#comment-<id>` grammar; its other
// fragments are fenced because the page reads the anchor to scroll. Nothing in the SowsView
// subtree reads either: `SowsView` destructures `{pathname, state}` only, and
// `RecordCollaborationSection`, `CommentsSection`, `RecordActivityLog`,
// `RecordPageShell`, `RecordSequenceNav`, `recordPageControls` and `Header`
// contain no `location.hash` / `location.search` / `useSearchParams` read at
// all. That is the same line `view.webformhub`, `view.clientErrors`,
// `view.pastureMap`, `view.animalHistory` and `view.weighins` already draw, for
// the same measured reason. The comment anchors CommentsSection renders
// (`id={'comment-' + c.id}`) are resolved by the browser, not by this page.
//
// RESERVED SEGMENTS: there are NONE beneath `/pig/sows/`. Derived over all
// seven route tables plus `APP_ROUTES_PRE_FORM`: no row in any of them declares
// a path nested under `/pig/sows`, and neither `ALIASES_EXACT` nor
// `ALIASES_PREFIX` targets anything at or beneath it. `SowsView` special-cases
// no id value. The static guard derives all three of those facts from the live
// tables rather than trusting this paragraph, so a future `/pig/sows/archive`
// row would redden it instead of silently widening the fence.
//
// WHY IT IS SAFE TO REFUSE MORE THAN A RELEASE AUDIT WOULD NEED. Refusing a
// shape costs no released capability: it keeps the raw route in the explicit
// recovery path until a later release lane can WIDEN the contract with browser
// evidence; it must never narrow it.
// ============================================================================

/** The registry list, and the canonical `VIEW_TO_PATH` target for view `sows`. */
export const PIG_SOWS_INDEX_PATHNAME = '/pig/sows';

/** The record sub-mode's prefix — the exact string SowsView slices on. */
export const PIG_SOWS_RECORD_PATHNAME_PREFIX = '/pig/sows/';

// One meaningful raw segment: no separator in either direction, no NUL, no line
// break, and no query/fragment marker smuggled into a bare pathname string.
function isSafeRawSegment(rawSegment: string): boolean {
  return !(
    rawSegment === '' ||
    rawSegment.includes('/') ||
    rawSegment.includes('\\') ||
    rawSegment.includes('\0') ||
    rawSegment.includes('\r') ||
    rawSegment.includes('\n') ||
    rawSegment.includes('?') ||
    rawSegment.includes('#')
  );
}

// The same rules again after ONE decode, which is what turns `%2F`, `%5C`,
// `%00`, `%0A`, `%0D`, `%2E`, and `%2E%2E` into rejections rather than into
// accepted opaque text. A malformed escape makes decodeURIComponent throw and
// the caller's catch returns false. SowsView performs exactly this one decode
// on exactly this segment, so the predicate and the page agree by construction.
function isSafeDecodedSegment(segment: string): boolean {
  return (
    segment.trim() !== '' &&
    segment !== '.' &&
    segment !== '..' &&
    !segment.includes('/') &&
    !segment.includes('\\') &&
    !segment.includes('\0') &&
    !segment.includes('\r') &&
    !segment.includes('\n') &&
    !segment.includes('?') &&
    !segment.includes('#')
  );
}

/**
 * `/pig/sows`, or `/pig/sows/<id>` with exactly one decoded-safe segment.
 *
 * The two clauses are the surface's two modes and nothing else. The splat base
 * `/pig/sows/` ends at the prefix and yields an empty raw segment, so it is
 * claimed by neither — and it is not a route any reviewer audited: SowsView
 * mounts `RecordPageNotFound` for it. The raw segment is checked BEFORE the
 * decoder so a literal separator, NUL, or line break never reaches it, then the
 * value is decoded once and re-checked so an encoded separator, dot segment,
 * NUL, or line break cannot widen the record release.
 */
export function isCanonicalPigSowsPathname(pathname: unknown): pathname is string {
  if (typeof pathname !== 'string') return false;
  if (pathname === PIG_SOWS_INDEX_PATHNAME) return true;
  if (!pathname.startsWith(PIG_SOWS_RECORD_PATHNAME_PREFIX)) return false;
  const rawSegment = pathname.slice(PIG_SOWS_RECORD_PATHNAME_PREFIX.length);
  if (!isSafeRawSegment(rawSegment)) return false;
  try {
    return isSafeDecodedSegment(decodeURIComponent(rawSegment));
  } catch {
    return false;
  }
}

/**
 * The resolver hands this the raw location, which may be a bare pathname string
 * or a location-like object. Only the pathname is consulted; see the header for
 * the measurement behind that, and note that the pathname predicate itself
 * refuses a `?` or `#` smuggled into a bare string.
 */
export function isCanonicalPigSowsLocation(location: unknown): boolean {
  if (typeof location === 'string') return isCanonicalPigSowsPathname(location);
  if (!location || typeof location !== 'object' || Array.isArray(location)) return false;
  return isCanonicalPigSowsPathname((location as Readonly<{pathname?: unknown}>).pathname);
}
